import { useRef, useState } from "react";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Center } from "@freightsimple/ui-core/PageLayout/Center";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";
import { captureException } from "@freightsimple/observability/errorReporting";
import { pdfjs, Document, Page } from "react-pdf";
// Bundle the pdf.js worker as one of our own assets. Fetching it from a third-party
// CDN at runtime meant executing externally-served JS in our origin and broke all
// PDF viewing whenever the CDN was unreachable. The legacy build matches the
// pdfjs-dist alias in vite.config.ts, so older browsers can open documents too.
import pdfWorkerSrc from "pdfjs-dist/legacy/build/pdf.worker.min.mjs?url";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

// The decoders, CMaps, fonts and colour profiles pdf.js fetches at runtime,
// served from our own origin by vite.config.ts. pdf.js needs absolute URLs, and
// resolving against the page keeps a relative BASE_URL working. Module-level so
// react-pdf doesn't see new options, and reload the document, on every render.
const pdfBaseUrl = new URL(
  `${import.meta.env.BASE_URL}pdfjs/`,
  window.location.href,
).href;
const pdfOptions = {
  wasmUrl: `${pdfBaseUrl}wasm/`,
  cMapUrl: `${pdfBaseUrl}cmaps/`,
  standardFontDataUrl: `${pdfBaseUrl}standard_fonts/`,
  iccUrl: `${pdfBaseUrl}iccs/`,
};

// Same height as the dialog's own loading state, so nothing jumps between the
// document request finishing and pdf.js finishing.
const loadingState = (
  <Center minHeight={400}>
    <Spinner size={48} />
  </Center>
);

const errorState = <Banner variant="danger">Failed to load document</Banner>;

// react-pdf's default asks for a password with window.prompt and asks again
// forever when it is cancelled. We can't take passwords here, so refuse: when
// onPassword throws, pdf.js fails the load with its own PasswordException,
// which shows the error and which reportPdfError leaves unreported.
function refusePassword(): never {
  throw new Error("The PDF is password protected");
}

interface PdfRendererProps {
  base64Data: string;
  currentPage: number;
  onTotalPagesChange: (totalPages: number) => void;
}

export function ShipmentPdfRenderer(props: PdfRendererProps) {
  const { base64Data, currentPage, onTotalPagesChange } = props;
  // react-pdf reports a payload it can't even decode (bad base64) only through
  // onSourceError and then keeps showing its loading state, and a page that
  // fails to draw only through onRenderError, leaving a blank canvas. Both are
  // shown here instead, keyed by what failed so a different document or page
  // gets a fresh try.
  const [failedSource, setFailedSource] = useState<string | undefined>();
  const [failedRender, setFailedRender] = useState<{
    source: string;
    page: number;
  }>();
  // A failing page fails again each time it is shown, so report each distinct
  // failure once per document.
  const reported = useRef({ source: base64Data, keys: new Set<string>() });

  function reportPdfError(action: string) {
    return (error: Error) => {
      // A password-protected PDF is the file, not a fault: it gets the error
      // banner but isn't reported.
      if (error.name === "PasswordException") return;
      if (reported.current.source !== base64Data) {
        reported.current = { source: base64Data, keys: new Set() };
      }
      const key = `${action}:${error.name}:${error.message}`;
      if (reported.current.keys.has(key)) return;
      reported.current.keys.add(key);
      captureException(error, { component: "ShipmentPdfRenderer", action });
    };
  }

  const renderFailed =
    failedRender?.source === base64Data && failedRender.page === currentPage;

  if (failedSource === base64Data) {
    return errorState;
  }

  return (
    // Suspense off: nothing between this dialog and the app root catches a
    // suspended or failed load, so a bad PDF would replace the whole page
    // instead of showing the loading and error states below.
    <Document
      file={`data:application/pdf;base64,${base64Data}`}
      options={pdfOptions}
      suspense={false}
      loading={loadingState}
      error={errorState}
      onPassword={refusePassword}
      onLoadSuccess={(pdf) => {
        onTotalPagesChange(pdf.numPages);
      }}
      onLoadError={reportPdfError("loadPdf")}
      onSourceError={(error) => {
        reportPdfError("readPdf")(error);
        setFailedSource(base64Data);
      }}
    >
      {renderFailed ? (
        errorState
      ) : (
        <Page
          pageNumber={currentPage}
          width={760}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          className="custom-page"
          loading={loadingState}
          error={errorState}
          onLoadError={reportPdfError("loadPage")}
          onRenderError={(error) => {
            reportPdfError("renderPage")(error);
            setFailedRender({ source: base64Data, page: currentPage });
          }}
        />
      )}
    </Document>
  );
}
